<template>
  <div class="text-center">
    <v-menu v-model="menu" :close-on-content-click="false" bottom offset-y>
      <template v-slot:activator="{ props }">
        <v-chip :color="selectedTagIds.length > 0 ? 'primary' : 'primary'" v-bind="props" label>
          {{ $t("common.action.tags") }}
          <span v-if="selectedTagIds.length > 0">&nbsp;|&nbsp;</span>
          <span class="font-weight-bold" v-if="selectedTagIds.length > 0">
            {{ selectedTagIds.length }}
          </span>
          <v-btn
            v-if="selectedTagIds.length > 0"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset()"
          />
        </v-chip>
      </template>

      <v-card min-width="350" class="mt-2">
        <v-list>
          <v-list-item :title="$t('page.product.filter.filterByTags')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <div v-if="loading" class="d-flex justify-center align-center pa-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <span class="ml-2">{{ $t("common.action.loading") }}</span>
        </div>

        <v-list class="mt-1 mx-6">
          <v-autocomplete
            v-model="selectedTagIds"
            :items="availableTags"
            :loading="loading"
            item-title="name"
            item-value="id"
            :label="$t('product.selectProductTags')"
            variant="outlined"
            density="comfortable"
            chips
            clearable
            multiple
            :return-object="false"
            :menu-props="{ maxHeight: 300 }"
          >
            <template v-slot:selection="{ item }">
              <v-chip size="small" class="ma-1">
                {{ getProductTagItem(item).name }}
              </v-chip>
            </template>

            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:title>
                  <product-tag-chip
                    :label="getProductTagItem(item).name"
                    :color="getProductTagItem(item).hexColor"
                  />
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>

          <!-- Match Type Switch -->
          <div class="d-flex align-center justify-space-between mt-4">
            <span class="text-body-2">{{ $t("common.filter.tagMatchType") }}</span>
            <v-switch
              v-model="exactMatch"
              :label="exactMatch ? $t('common.filter.exactMatch') : $t('common.filter.anyMatch')"
              color="primary"
              hide-details
              density="compact"
              inset
            ></v-switch>
          </div>
          <div class="text-caption text-grey mt-1">
            {{ exactMatch ? $t("common.filter.exactMatchHint") : $t("common.filter.anyMatchHint") }}
          </div>
        </v-list>

        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="
              menu = false;
              updateFilterProducts();
            "
            color="primary"
            class="mr-2"
          >
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from "vue";
import { useProductsStore } from "@/catalog/stores/products";
import { useProductClassesStore } from "@/catalog/stores/productClasses";
import { useBundleProductTagsStore } from "@/catalog/stores/bundleProductTags";
import { ProductTagItem } from "@/catalog/types/productTags";
import ProductTagChip from "@/catalog/components/product-tag/ProductTagChip.vue";
const props = defineProps({
  context: {
    type: String,
    default: "product",
    validator: (value: string) => ["product", "product-class"].includes(value),
  },
});

const menu = ref(false);
const loading = ref(false);
const availableTags = ref<ProductTagItem[]>([]);
const selectedTagIds = ref<string[]>([]);
const exactMatch = ref(false);

const getProductTagItem = (item: ProductTagItem | { raw?: ProductTagItem }) =>
  ("raw" in item && item.raw ? item.raw : item) as ProductTagItem;

const productsStore = useProductsStore();
const productClassesStore = useProductClassesStore();
const { setFilterProductCondition } = productsStore;
const { setFilterProductClassCondition } = productClassesStore;

// Fetch all active product tags when component mounts
const fetchProductTags = async () => {
  loading.value = true;

  try {
    await useBundleProductTagsStore().fetchProductTags();
    availableTags.value = useBundleProductTagsStore().getActiveProductTags;

    // After fetching tags, restore any previously selected tags from the filter condition
    restoreTagSelection();
  } finally {
    loading.value = false;
  }
};

// New method to restore tag selection from store
const restoreTagSelection = () => {
  const filterCondition =
    props.context === "product-class"
      ? productClassesStore.filterProductClassCondition
      : productsStore.filterProductCondition;

  if (filterCondition && filterCondition.tags && Array.isArray(filterCondition.tags)) {
    // Check if all the tag IDs in the filter are valid tags in our list
    const validTagIds = availableTags.value.map((tag) => tag.id);
    const validSelectedTags = filterCondition.tags.filter((id: string) => validTagIds.includes(id));

    selectedTagIds.value = [...validSelectedTags];

    // Also restore the exact match setting if it exists
    if (typeof filterCondition.exactTagMatch === "boolean") {
      exactMatch.value = filterCondition.exactTagMatch;
    }
  } else {
    // Reset selection if no tags in the filter
    selectedTagIds.value = [];
    exactMatch.value = false;
  }
};

onMounted(() => {
  fetchProductTags();
});

watch(
  () =>
    props.context === "product-class"
      ? productClassesStore.filterProductClassCondition
      : productsStore.filterProductCondition,
  () => {
    restoreTagSelection();
  },
  { deep: true },
);

const reset = async () => {
  selectedTagIds.value = [];

  if (props.context === "product-class") {
    const filterProductClassCondition = {
      ...productClassesStore.filterProductClassCondition,
    };
    delete filterProductClassCondition.tags;
    delete filterProductClassCondition.exactTagMatch;
    setFilterProductClassCondition(filterProductClassCondition);
    await productClassesStore.searchProductClasses({}, false);
    return;
  }

  const filterProductCondition = productsStore.filterProductCondition;
  delete filterProductCondition.tags;
  delete filterProductCondition.exactTagMatch;
  setFilterProductCondition(filterProductCondition);
  await productsStore.searchProducts({}, false);
};

const updateFilterProducts = async () => {
  if (selectedTagIds.value.length === 0) {
    await reset();
    return;
  }

  if (props.context === "product-class") {
    const oldCondition = productClassesStore.filterProductClassCondition;
    const newCondition = {
      ...oldCondition,
      tags: selectedTagIds.value,
      exactTagMatch: exactMatch.value,
    };

    setFilterProductClassCondition(newCondition);
    await productClassesStore.searchProductClasses({}, false);
    return;
  }

  const oldCondition = productsStore.filterProductCondition;
  const newCondition = {
    ...oldCondition,
    tags: selectedTagIds.value,
    exactTagMatch: exactMatch.value,
  };

  setFilterProductCondition(newCondition);
  await productsStore.searchProducts({}, false);
};
</script>
